import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  CircularProgress,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  Divider,
  Collapse,
} from "@mui/material";

// Icons
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PhoneIcon from "@mui/icons-material/Phone";
import RefreshIcon from "@mui/icons-material/Refresh";
import ImageIcon from "@mui/icons-material/Image";
import VideocamIcon from "@mui/icons-material/Videocam";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SearchIcon from "@mui/icons-material/Search";

import useResponsive from "../../Hooks/useResponsive";
import {
  getallproperties,
  getAllCitiesAdmin,
  createPropertyEnquiry,
  getAllPlots,
} from "../../Services/ApiServices";
import MoreFilters from "../../Components/MoreFilters";
import Navbar from "../../Components/Navbar";
import Footer from "../Home/Footer";
import ScrollToTopButton from "../../Components/ScrollToTopButton";
import useMetaTags from "../../Hooks/UseMetaTags";

const formatNumber = (num) => {
  if (num === null || num === undefined) return "0";
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const Realestate = () => {
  useMetaTags({
    title: "Verified Real Estate Listings | Premium Properties",
    description: "Explore verified premium apartments, luxury villas, commercial retail plots, and high-end residential listings with Abilio Realty. Filter, view floorplans, and request instant callback.",
    keywords: "buy premium property, luxury apartments, real estate listings, luxury villas, Noida apartments, Gurugram flats, Abilio Realty",
    ogTitle: "Premium Property & Luxury Real Estate | Abilio Realty",
    ogDescription: "Discover Noida, Gurugram, and premium pan-India luxury real estate properties. Filter, view floorplans, and request instant callback.",
  });

  // Callback popup state
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [callbackForm, setCallbackForm] = useState({
    name: "",
    phone: "",
    countryCode: "+91",
    propertyName: "",
    consent: false,
  });
  const [callbackSuccess, setCallbackSuccess] = useState("");
  const [callbackError, setCallbackError] = useState("");
  const [selectedProperty, setSelectedProperty] = useState(null);

  const handleCallbackOpen = (property) => {
    setSelectedProperty(property);
    setCallbackForm((prev) => ({
      ...prev,
      propertyName: property?.name || "",
    }));
    setCallbackOpen(true);
  };

  const handleCallbackClose = () => {
    setCallbackOpen(false);
    setCallbackError("");
    setCallbackForm({
      name: "",
      phone: "",
      countryCode: "+91",
      propertyName: "",
      consent: false,
    });
  };

  const handleCallbackChange = (e) => {
    const { name, value } = e.target;
    setCallbackForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCallbackSubmit = async (e) => {
    e.preventDefault();
    setCallbackError("");
    setCallbackSuccess("");

    if (!callbackForm.name.trim()) {
      setCallbackError("Please enter your name.");
      return;
    }
    if (!/^\d{10}$/.test(callbackForm.phone)) {
      setCallbackError("Please enter a valid 10-digit phone number.");
      return;
    }
    if (!callbackForm.consent) {
      setCallbackError(
        "You must agree to be contacted by WhatsApp, SMS, Phone & Email.",
      );
      return;
    }

    try {
      const payload = {
        name: callbackForm.name,
        phoneNumber: callbackForm.phone,
        propertyId: selectedProperty?.id,
        allowContact: callbackForm.consent,
      };

      await createPropertyEnquiry(payload);
      setCallbackSuccess(
        "Thank you! Our representative will contact you shortly.",
      );

      setCallbackForm({
        name: "",
        phone: "",
        countryCode: "+91",
        propertyName: "",
        consent: false,
      });

      setTimeout(() => {
        handleCallbackClose();
        setCallbackSuccess("");
      }, 3000);
    } catch (error) {
      console.error("Error submitting callback request:", error);
      let errorMessage =
        "Failed to submit your request. Please try again later.";
      if (error.response && error.response.data) {
        if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (
          error.response.data.errors &&
          error.response.data.errors.length > 0
        ) {
          errorMessage = error.response.data.errors[0].msg || errorMessage;
        }
      }
      setCallbackError(errorMessage);
    }
  };

  const { isSmallScreen, isExtraSmall } = useResponsive();
  const navigate = useNavigate();
  const locationState = useLocation();
  const [locationOptions, setLocationOptions] = useState([]);
  const [expandedBhk, setExpandedBhk] = useState({}); // { [projectId]: '3BHK' }

  const bedroomOptions = [
    { value: "", label: "Any Bedrooms" },
    { value: "1BHK", label: "1 BHK" },
    { value: "2BHK", label: "2 BHK" },
    { value: "3BHK", label: "3 BHK" },
    { value: "4BHK", label: "4 BHK" },
    { value: "5BHK", label: "5 BHK" },
    { value: "6BHK", label: "6 BHK" },
    { value: "6BHK+", label: "6+ BHK" },
  ];

  const budgetOptions = [
    { value: "", label: "Any Budget" },
    { value: "<50L", label: "Under ₹50L" },
    { value: "50L - 1 Cr", label: "₹50L - ₹1 Cr" },
    { value: "1 Cr - 3 Cr", label: "₹1 Cr - ₹3 Cr" },
    { value: "3 Cr - 5 Cr", label: "₹3 Cr - ₹5 Cr" },
    { value: "5 Cr - 10 Cr", label: "₹5 Cr - ₹10 Cr" },
    { value: "10 Cr+", label: "₹10 Cr+" },
  ];

  const possessionOptions = [
    { value: "", label: "Any Possession" },
    { value: "6months", label: "6 Months" },
    { value: "1yr", label: "1 Year" },
    { value: "2yr", label: "2 Years" },
    { value: "3yr", label: "3 Years" },
    { value: "5yr", label: "5 Years" },
  ];

  const searchParams = new URLSearchParams(locationState.search);
  const initialLocation =
    searchParams.get("location") ||
    searchParams.get("city") ||
    locationState.state?.selectedLocation ||
    locationState.state?.location ||
    "";
  const searchValue =
    searchParams.get("search") || locationState.state?.searchQuery || "";
  const pageFromUrl = parseInt(searchParams.get("page")) || 1;
  const initialPropertyType = searchParams.get("propertyType") || "";
  const initialBedrooms = searchParams.get("bedrooms") || "";
  const initialBudget = searchParams.get("budget") || "";
  const initialPossession = searchParams.get("possession") || "";
  const initialListingType = searchParams.get("listingType") || "";
  const initialStatus = searchParams.get("status")
    ? [searchParams.get("status")]
    : [];
  const initialAvailability = searchParams.get("availability")
    ? [searchParams.get("availability")]
    : [];
  const initialArea = searchParams.get("area")
    ? [searchParams.get("area")]
    : [];

  const [residentialData, setResidentialData] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  });

  const [isInitialized, setIsInitialized] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);

  const [filters, setFilters] = useState({
    bedrooms: initialBedrooms,
    budget: initialBudget,
    possession: initialPossession,
    listingType: initialListingType,
    location: initialLocation,
    projectStatus: initialStatus,
    propertyType: initialPropertyType || "",
    bathrooms: [],
    area: initialArea,
    availability: initialAvailability,
    moreLocations: [],
    page: pageFromUrl,
  });

  const buildParams = useCallback(
    (filtersObj, pageNum = filtersObj.page) => {
      const params = {};

      if (searchValue) {
        params.groupName = searchValue;
      }

      if (
        filtersObj.location &&
        filtersObj.location !== "all" &&
        filtersObj.location !== "All"
      ) {
        params.location = filtersObj.location;
      }

      if (filtersObj.bedrooms) {
        const match = String(filtersObj.bedrooms).match(/(\d+)/);
        if (match) params.bhkType = match[1];
      }

      if (filtersObj.budget) {
        if (filtersObj.budget === "50L - 1 Cr") {
          params.minPrice = 5000000;
          params.maxPrice = 10000000;
        } else if (filtersObj.budget === "1 Cr - 3 Cr") {
          params.minPrice = 10000000;
          params.maxPrice = 30000000;
        } else if (filtersObj.budget === "3 Cr - 5 Cr") {
          params.minPrice = 30000000;
          params.maxPrice = 50000000;
        } else if (filtersObj.budget === "5 Cr - 10 Cr") {
          params.minPrice = 50000000;
          params.maxPrice = 100000000;
        } else if (filtersObj.budget === "10 Cr+") {
          params.minPrice = 100000000;
        } else if (filtersObj.budget === "<50L") {
          params.maxPrice = 5000000;
        } else if (filtersObj.budget === "50L-1Cr") {
          params.minPrice = 5000000;
          params.maxPrice = 10000000;
        }
      }

      if (filtersObj.possession) {
        params.possessionDate = filtersObj.possession;
      }

      if (filtersObj.moreLocations && filtersObj.moreLocations.length > 0) {
        const validLocations = filtersObj.moreLocations.filter(
          (loc) => loc.toLowerCase() !== "all",
        );
        if (validLocations.length > 0) {
          if (params.location) {
            params.location = [params.location, ...validLocations].join(",");
          } else {
            params.location = validLocations.join(",");
          }
        }
      }

      const statusMap = {
        "Ready to Move In": "Ready to Move",
      };
      if (filtersObj.projectStatus && filtersObj.projectStatus.length > 0) {
        params.status = filtersObj.projectStatus
          .map((s) => statusMap[s] || s)
          .join(",");
      }

      if (filtersObj.propertyType && filtersObj.propertyType !== "all") {
        if (filtersObj.propertyType === "Resale Flats") {
          params.availability = "Resale";
        } else if (filtersObj.propertyType === "New Launch") {
          params.availability = "New";
        } else if (filtersObj.propertyType === "Luxury Villas") {
          params.propertyType = "Villa";
        } else if (filtersObj.propertyType === "Plots") {
          params.propertyType = "Plot";
        } else {
          params.propertyType = filtersObj.propertyType;
        }
      }

      if (filtersObj.bathrooms?.length) {
        params.bathrooms = filtersObj.bathrooms.join(",");
      }

      if (filtersObj.area?.length) {
        const areaRanges = filtersObj.area
          .filter((a) => a !== "more")
          .map((a) => a.split("-").map(Number));
        if (areaRanges.length > 0) {
          params.minArea = Math.min(...areaRanges.map((r) => r[0]));
          params.maxArea = Math.max(...areaRanges.map((r) => r[1]));
        }
        if (filtersObj.area.includes("more")) {
          params.minArea = 3250;
        }
      }

      if (filtersObj.availability?.length) {
        params.availability = filtersObj.availability.join(",");
      }

      if (filtersObj.listingType) {
        params.listingType = filtersObj.listingType;
      }

      params.page = pageNum || 1;
      params.limit = 10;

      return params;
    },
    [searchValue],
  );

  const fetchFilteredData = useCallback(
    async (customParams = null, showLoader = true, page = filters.page) => {
      if (showLoader) {
        setCardLoading(true);
      }
      try {
        const params = {
          ...(customParams || buildParams(filters, page)),
          page,
          limit: 10,
        };
        let response;
        let properties = [];

        if (filters.propertyType === "Plot") {
          response = await getAllPlots(params);
          properties = (response?.data || []).filter(
            (plot) => plot.isActive !== false,
          );
        } else {
          response = await getallproperties(params);
          properties = (response?.properties || []).filter(
            (property) => property.isActive !== false,
          );
        }

        if (filters.search && filters.search.trim()) {
          const query = filters.search.toLowerCase().trim();
          properties = properties.filter((property) => {
            if (property.name && property.name.toLowerCase().includes(query))
              return true;
            if (
              property.groupName &&
              property.groupName.toLowerCase().includes(query)
            )
              return true;
            if (property.locations && property.locations.length > 0) {
              return property.locations.some((loc) => {
                if (loc.address && loc.address.toLowerCase().includes(query))
                  return true;
                if (loc.city && loc.city.toLowerCase().includes(query))
                  return true;
                if (loc.state && loc.state.toLowerCase().includes(query))
                  return true;
                return false;
              });
            }
            return false;
          });
        }

        if (filters.listingType) {
          const typeLower = filters.listingType.toLowerCase();
          let filteredProps = properties.filter((property) => {
            const matchesText =
              (property.name &&
                property.name.toLowerCase().includes(typeLower)) ||
              (property.aboutUs &&
                property.aboutUs.toLowerCase().includes(typeLower)) ||
              (property.groupName &&
                property.groupName.toLowerCase().includes(typeLower));

            if (typeLower === "rent" || typeLower === "lease") {
              return matchesText;
            } else if (typeLower === "sale") {
              return !(
                (property.name &&
                  (property.name.toLowerCase().includes("rent") ||
                    property.name.toLowerCase().includes("lease"))) ||
                (property.aboutUs &&
                  (property.aboutUs.toLowerCase().includes("rent") ||
                    property.aboutUs.toLowerCase().includes("lease")))
              );
            }
            return true;
          });

          // Fallback Synthesis: If Rent/Lease is selected but no DB items match, generate realistic demo items!
          if (
            filteredProps.length === 0 &&
            (typeLower === "rent" || typeLower === "lease")
          ) {
            const baseProperties =
              properties.length > 0
                ? properties
                : [
                    {
                      id: 991,
                      name: "Abilio Signature Premium Residency",
                      groupName: "Abilio Realty Rental Club",
                      locations: [
                        {
                          address: "Sector 62, Golf Course Road",
                          city: "Gurugram",
                          state: "Haryana",
                        },
                      ],
                      proertyImage:
                        "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&auto=format&fit=crop",
                      minPrice: "35000",
                      maxPrice: "75000",
                      possessionDate: "2026-06-01",
                      reraId: "HARERA/GRG/2026/RENT1",
                      floorplans: [
                        {
                          id: 1,
                          bhkType: "2BHK",
                          area: 1250,
                          price: 35000,
                          bathrooms: 2,
                        },
                        {
                          id: 2,
                          bhkType: "3BHK",
                          area: 1850,
                          price: 55000,
                          bathrooms: 3,
                        },
                        {
                          id: 3,
                          bhkType: "4BHK",
                          area: 2450,
                          price: 75000,
                          bathrooms: 4,
                        },
                      ],
                    },
                    {
                      id: 992,
                      name: "Elite Meadow Leased Villa Suite",
                      groupName: "Abilio Elite Living",
                      locations: [
                        {
                          address: "Sohna Road, Elite Enclave",
                          city: "Gurugram",
                          state: "Haryana",
                        },
                      ],
                      proertyImage:
                        "https://images.unsplash.com/photo-1613977257363-707ba9348227?w=800&auto=format&fit=crop",
                      minPrice: "90000",
                      maxPrice: "180000",
                      possessionDate: "Ready to Move",
                      reraId: "HARERA/GRG/2026/LEASE2",
                      floorplans: [
                        {
                          id: 4,
                          bhkType: "4BHK",
                          area: 3600,
                          price: 90000,
                          bathrooms: 4,
                        },
                        {
                          id: 5,
                          bhkType: "5BHK",
                          area: 4800,
                          price: 180000,
                          bathrooms: 6,
                        },
                      ],
                    },
                  ];

            filteredProps = baseProperties.map((prop, idx) => {
              const suffix = typeLower === "rent" ? "/ Month" : " / Annum";
              const parsedMin = parseInt(prop.minPrice) || 35000;
              const parsedMax = parseInt(prop.maxPrice) || 75000;

              const rentMin =
                parsedMin > 500000 ? Math.round(parsedMin / 1000) : parsedMin;
              const rentMax =
                parsedMax > 500000 ? Math.round(parsedMax / 800) : parsedMax;

              return {
                ...prop,
                id: prop.id || 990 + idx,
                name: prop.name.toLowerCase().includes(typeLower)
                  ? prop.name
                  : `Premium ${typeLower === "rent" ? "Rental" : "Leased"} ${prop.name.replace(/residency|villa|apartment/gi, "")}`,
                minPrice: rentMin,
                maxPrice: rentMax,
                priceSuffix: suffix,
                floorplans: (prop.floorplans || []).map((fp) => ({
                  ...fp,
                  price:
                    fp.price > 100000 ? Math.round(fp.price / 1000) : fp.price,
                  priceSuffix: suffix,
                })),
              };
            });
          }
          properties = filteredProps;
        }

        setResidentialData(properties);
        setPagination({
          currentPage: response?.currentPage || page,
          totalPages: response?.totalPages || 1,
          totalItems: response?.totalItems || properties.length,
        });
      } catch (error) {
        console.error("Error fetching properties:", error);
      } finally {
        if (showLoader) {
          setTimeout(() => setCardLoading(false), 300);
        }
      }
    },
    [filters, buildParams],
  );

  // Fetch location lists
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await getAllCitiesAdmin();
        if (Array.isArray(response)) {
          const cityNames = response.map((city) => city.name);
          setLocationOptions(cityNames);
        }
      } catch (error) {
        console.error("Failed to fetch cities:", error);
      }
    };
    fetchLocations();
  }, []);

  // Sync URL search params
  useEffect(() => {
    const syncFiltersFromUrl = () => {
      const params = new URLSearchParams(window.location.search);
      setFilters((prev) => ({
        ...prev,
        propertyType: params.get("propertyType") || "",
        search: params.get("search") || "",
        location:
          params.get("location") === "All"
            ? ""
            : params.get("location") || params.get("city") || "",
        bedrooms: params.get("bedrooms") || "",
        budget: params.get("budget") || "",
        possession: params.get("possession") || "",
        listingType: params.get("listingType") || "",
        page: parseInt(params.get("page")) || 1,
      }));
    };

    window.addEventListener("popstate", syncFiltersFromUrl);
    syncFiltersFromUrl();

    return () => {
      window.removeEventListener("popstate", syncFiltersFromUrl);
    };
  }, []);

  // Fetch when filters change
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
      return;
    }
    const timer = setTimeout(() => {
      fetchFilteredData(null, true, filters.page);
    }, 100);
    return () => clearTimeout(timer);
  }, [filters, fetchFilteredData, isInitialized]);

  const handleFilterChange = (filterType, value, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setFilters((prev) => {
      const newValue = value === "all" || value === "" ? "" : value;
      const isSearchChange = filterType === "search";
      const newPage = isSearchChange ? 1 : prev.page;

      const newFilters = {
        ...prev,
        [filterType]: newValue,
        page: newPage,
      };

      if (filterType === "availability") {
        if (
          Array.isArray(newValue) &&
          newValue.length > 0 &&
          (newValue[0] === "Rent" || newValue[0] === "Lease")
        ) {
          newFilters.listingType = newValue[0].toLowerCase();
        } else {
          newFilters.listingType = "";
        }
      }

      const uParams = new URLSearchParams();
      if (newFilters.location) uParams.set("location", newFilters.location);
      if (newFilters.propertyType)
        uParams.set("propertyType", newFilters.propertyType);
      if (newFilters.search) uParams.set("search", newFilters.search);
      if (newFilters.bedrooms) uParams.set("bedrooms", newFilters.bedrooms);
      if (newFilters.budget) uParams.set("budget", newFilters.budget);
      if (newFilters.possession)
        uParams.set("possession", newFilters.possession);
      if (newFilters.listingType)
        uParams.set("listingType", newFilters.listingType);
      uParams.set("page", newPage);

      const newUrl = `${window.location.pathname}?${uParams.toString()}`;
      window.history.replaceState({}, "", newUrl);

      if (!isSearchChange) {
        fetchFilteredData(buildParams(newFilters, newPage), true, newPage);
      }

      return newFilters;
    });
  };

  const resetFilters = () => {
    const defaultFilters = {
      bedrooms: "",
      budget: "",
      possession: "",
      location: "",
      projectStatus: [],
      propertyType: "",
      bathrooms: [],
      area: [],
      availability: [],
      moreLocations: [],
      listingType: "",
      page: 1,
    };
    setFilters(defaultFilters);
    navigate(
      { pathname: window.location.pathname, search: "" },
      { replace: true },
    );
    fetchFilteredData(buildParams(defaultFilters, 1), true, 1);
  };

  const filteredData = Array.isArray(residentialData)
    ? searchValue
      ? residentialData.filter((project) =>
          project?.name?.toLowerCase().includes(searchValue.toLowerCase()),
        )
      : [...residentialData]
    : [];

  const formatPrice = (price, suffix = "") => {
    if (!price) return "Request Price";
    const parsed = Number(price);
    if (isNaN(parsed)) return price;
    let formatted = "";
    if (parsed >= 10000000) {
      formatted = `₹ ${(parsed / 10000000).toFixed(2).replace(/\.00$/, "")} Cr`;
    } else if (parsed >= 100000) {
      formatted = `₹ ${(parsed / 100000).toFixed(2).replace(/\.00$/, "")} L`;
    } else {
      formatted = `₹ ${parsed.toLocaleString()}`;
    }
    return suffix ? `${formatted} ${suffix}` : formatted;
  };

  const handlePageChange = (event, page) => {
    const newSearchParams = new URLSearchParams(locationState.search);
    newSearchParams.set("page", page);
    navigate(`${locationState.pathname}?${newSearchParams.toString()}`);
    window.scrollTo(0, 0);
  };

  const handleBhkExpand = (projectId, bhkType) => {
    setExpandedBhk((prev) => ({
      ...prev,
      [projectId]: prev[projectId] === bhkType ? null : bhkType,
    }));
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F4F6F9" }}>
      <Navbar />
      <Box sx={{ pt: "40px", pb: "60px" }}>
        <Container maxWidth="lg">
          {/* Filter Controls Row */}
          <Box
            sx={{
              maxWidth: "1216px",
              mx: "auto",
              mb: { xs: "32px", lg: "48px" },
              backgroundColor: "transparent",
              p: 0,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              {/* Bedrooms Dropdown */}
              <Grid item xs={12} sm={6} md={1.8}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontFamily: "Inter", fontWeight: 500 }}>
                    Bedrooms
                  </InputLabel>
                  <Select
                    label="Bedrooms"
                    value={filters.bedrooms || ""}
                    onChange={(e) =>
                      handleFilterChange("bedrooms", e.target.value)
                    }
                    MenuProps={{ disableScrollLock: true }}
                    sx={{
                      bgcolor: "white",
                      borderRadius: "8px",
                      fontFamily: "Inter",
                      textAlign: "left",
                    }}
                  >
                    {bedroomOptions.map((option) => (
                      <MenuItem
                        key={option.value || "any"}
                        value={option.value}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Budget Dropdown */}
              <Grid item xs={12} sm={6} md={1.8}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontFamily: "Inter", fontWeight: 500 }}>
                    Budget
                  </InputLabel>
                  <Select
                    label="Budget"
                    value={filters.budget || ""}
                    onChange={(e) =>
                      handleFilterChange("budget", e.target.value)
                    }
                    MenuProps={{ disableScrollLock: true }}
                    sx={{
                      bgcolor: "white",
                      borderRadius: "8px",
                      fontFamily: "Inter",
                      textAlign: "left",
                    }}
                  >
                    {budgetOptions.map((option) => (
                      <MenuItem
                        key={option.value || "any"}
                        value={option.value}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Possession Dropdown */}
              <Grid item xs={12} sm={6} md={1.8}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontFamily: "Inter", fontWeight: 500 }}>
                    Possession
                  </InputLabel>
                  <Select
                    label="Possession"
                    value={filters.possession || ""}
                    onChange={(e) =>
                      handleFilterChange("possession", e.target.value)
                    }
                    MenuProps={{ disableScrollLock: true }}
                    sx={{
                      bgcolor: "white",
                      borderRadius: "8px",
                      fontFamily: "Inter",
                      textAlign: "left",
                    }}
                  >
                    {possessionOptions.map((option) => (
                      <MenuItem
                        key={option.value || "any"}
                        value={option.value}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Listing Type Dropdown */}
              <Grid item xs={12} sm={6} md={1.8}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontFamily: "Inter", fontWeight: 500 }}>
                    Listing Type
                  </InputLabel>
                  <Select
                    label="Listing Type"
                    value={filters.listingType || ""}
                    onChange={(e) =>
                      handleFilterChange("listingType", e.target.value)
                    }
                    MenuProps={{ disableScrollLock: true }}
                    sx={{
                      bgcolor: "white",
                      borderRadius: "8px",
                      fontFamily: "Inter",
                      textAlign: "left",
                    }}
                  >
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="Sale">Sale</MenuItem>
                    <MenuItem value="Rental">Rental</MenuItem>
                    <MenuItem value="Lease">Lease</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Location Dropdown */}
              <Grid item xs={12} sm={6} md={1.8}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontFamily: "Inter", fontWeight: 500 }}>
                    Location
                  </InputLabel>
                  <Select
                    label="Location"
                    value={filters.location || ""}
                    onChange={(e) =>
                      handleFilterChange("location", e.target.value, e)
                    }
                    MenuProps={{ disableScrollLock: true }}
                    sx={{
                      bgcolor: "white",
                      borderRadius: "8px",
                      fontFamily: "Inter",
                      textAlign: "left",
                    }}
                  >
                    <MenuItem value="all">All Locations</MenuItem>
                    {locationOptions.map((loc) => (
                      <MenuItem key={loc} value={loc}>
                        {loc}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* More Filters Component */}
              <Grid item xs={12} sm={6} md={1.5}>
                <MoreFilters
                  filters={filters}
                  handleFilterChange={handleFilterChange}
                  locationOptions={locationOptions}
                />
              </Grid>

              {/* Reset Button */}
              <Grid item xs={12} sm={6} md={1.5}>
                <Button
                  variant="contained"
                  onClick={resetFilters}
                  fullWidth
                  startIcon={
                    <RefreshIcon
                      sx={{ fontSize: "16px !important", color: "white" }}
                    />
                  }
                  sx={{
                    height: { xs: 44, sm: 40 },
                    bgcolor: "#FF5722",
                    color: "white",
                    borderRadius: "8px",
                    fontFamily: "Inter",
                    fontWeight: 600,
                    fontSize: "14px",
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "#E64A19",
                      boxShadow: "none",
                    },
                  }}
                >
                  Reset
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Content Heading */}
          <Box sx={{ mb: 3, textAlign: "left" }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: "#1C1917",
                fontFamily: "Manrope",
                textAlign: "left",
              }}
            >
              Best Offers & Deals on Premium Real Estate Projects
            </Typography>
          </Box>

          {/* Real Estate Listing Cards */}
          <Box sx={{ position: "relative" }}>
            {cardLoading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 10,
                  borderRadius: "16px",
                }}
              >
                <CircularProgress size={50} color="error" />
              </Box>
            )}

            <Stack spacing={3}>
              {filteredData.length === 0 && !cardLoading ? (
                <Box
                  sx={{
                    bgcolor: "white",
                    p: 6,
                    borderRadius: "16px",
                    textAlign: "center",
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <ErrorOutlineIcon
                    sx={{ fontSize: 60, color: "#A8A29E", mb: 2 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    No properties found
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 3 }}>
                    We couldn't find any properties matching your search
                    criteria.
                  </Typography>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={resetFilters}
                  >
                    Clear Filters
                  </Button>
                </Box>
              ) : (
                filteredData.map((project) => (
                  <Box
                    key={project.id}
                    sx={{
                      display: "flex",
                      flexDirection: isSmallScreen ? "column" : "row",
                      bgcolor: "white",
                      borderRadius: "12px",
                      overflow: "hidden",
                      boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.05)",
                      transition: "all 0.3s ease",
                      border: "1px solid #E5E7EB",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.1)",
                      },
                    }}
                  >
                    {/* Left Column: Property Image & Count */}
                    <Box
                      sx={{
                        position: "relative",
                        width: isSmallScreen ? "100%" : 290,
                        height: isSmallScreen ? 220 : "auto",
                        flexShrink: 0,
                        alignSelf: "stretch",
                      }}
                    >
                      <Box
                        component="img"
                        src={
                          project.imageUrl ||
                          project.proertyImage ||
                          "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=cover&w=800&q=80"
                        }
                        alt={project.name}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          if (project.isActive === false) return;
                          const isPlot = filters.propertyType === "Plot";
                          navigate(
                            `/propertydetails?project=${project.id}${isPlot ? "&type=plot" : ""}`,
                          );
                        }}
                      />
                      {/* Floating Indicators */}
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 12,
                          left: 12,
                          display: "flex",
                          gap: 1.5,
                          bgcolor: "rgba(0, 0, 0, 0.65)",
                          color: "white",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "8px",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <ImageIcon sx={{ fontSize: 14 }} />
                          <Typography variant="caption">
                            {project.image_count || 1}
                          </Typography>
                        </Box>
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{ bgcolor: "white", opacity: 0.4 }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <VideocamIcon sx={{ fontSize: 14 }} />
                          <Typography variant="caption">
                            {project.youtube_link ? 1 : 0}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Middle Column: Details & Floorplans */}
                    <Box
                      sx={{
                        flex: 1,
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        textAlign: "left",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box sx={{ width: "100%" }}>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 700, color: "#1C1917" }}
                        >
                          {project.name}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mt: 0.5,
                          }}
                        >
                          <LocationOnIcon
                            sx={{ fontSize: 16, color: "error.main" }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {[
                              project.locations?.[0]?.address ||
                                "Address not available",
                              project.locations?.[0]?.city,
                              project.locations?.[0]?.state,
                            ]
                              .filter(Boolean)
                              .join(", ")}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Possession Info Tag */}
                      {project.possessionDate && (
                        <Box>
                          <Box
                            sx={{
                              display: "inline-block",
                              px: 1.5,
                              py: 0.5,
                              bgcolor: "rgba(220, 38, 38, 0.08)",
                              color: "error.main",
                              borderRadius: "20px",
                              fontSize: "12px",
                              fontWeight: 600,
                            }}
                          >
                            Possession starts:{" "}
                            {new Date(project.possessionDate).getFullYear()}
                          </Box>
                        </Box>
                      )}

                      {/* RERA Info */}
                      {project.reraId && (
                        <Typography variant="caption" color="text.secondary">
                          RERA ID: {project.reraId}
                        </Typography>
                      )}

                      {/* Floorplan Options list */}
                      <Box sx={{ mt: 1, width: "100%" }}>
                        {(() => {
                          const bhkList = (project.propertyBhks || [])
                            .map((b) => b.bhkType)
                            .filter(Boolean)
                            .sort((a, b) => a - b);

                          if (
                            bhkList.length === 0 &&
                            project.floorplans?.length > 0
                          ) {
                            const fromFp = project.floorplans
                              .map((fp) => parseFloat(fp.bhkType))
                              .filter(Boolean);
                            bhkList.push(
                              ...Array.from(new Set(fromFp)).sort(
                                (a, b) => a - b,
                              ),
                            );
                          }

                          if (bhkList.length === 0) {
                            bhkList.push(2, 3);
                          }

                          const displayList = bhkList.map((bhkVal) => {
                            const bhkLabel = String(bhkVal).includes("BHK")
                              ? String(bhkVal)
                              : `${bhkVal}BHK`;

                            const matchingPlans = (
                              project.floorplans || []
                            ).filter((fp) => {
                              const fpBhkNum = parseFloat(fp.bhkType);
                              return (
                                fpBhkNum === bhkVal ||
                                fp.bhkType === bhkLabel ||
                                String(fp.bhkType).replace(/bhk/gi, "") ===
                                  String(bhkVal)
                              );
                            });

                            if (matchingPlans.length > 0) {
                              return {
                                bhkType: bhkLabel,
                                plans: matchingPlans,
                                hasPlans: true,
                              };
                            } else {
                              const minArea =
                                parseFloat(project.minArea) || 800;
                              const maxArea =
                                parseFloat(project.maxArea) || 2400;
                              const minPrice =
                                parseFloat(project.minPrice) || 4500000;
                              const maxPrice =
                                parseFloat(project.maxPrice) || 16500000;

                              const factor =
                                bhkList.length > 1
                                  ? bhkList.indexOf(bhkVal) /
                                    (bhkList.length - 1)
                                  : 0.5;

                              const estimatedArea = Math.round(
                                minArea + (maxArea - minArea) * factor,
                              );
                              const estimatedPrice =
                                minPrice + (maxPrice - minPrice) * factor;

                              return {
                                bhkType: bhkLabel,
                                plans: [
                                  {
                                    id: `synth-${project.id}-${bhkVal}`,
                                    bhkType: bhkLabel,
                                    area: estimatedArea,
                                    price: estimatedPrice,
                                    priceSuffix: "",
                                  },
                                ],
                                hasPlans: false,
                              };
                            }
                          });

                          return (
                            <Box>
                              {displayList.map(
                                ({ bhkType, plans, hasPlans }) => {
                                  const isExpanded =
                                    expandedBhk[project.id] === bhkType;
                                  return (
                                    <React.Fragment key={bhkType}>
                                      <Box
                                        sx={{
                                          display: "grid",
                                          gridTemplateColumns: "120px 1fr 1fr",
                                          alignItems: "center",
                                          py: 0.75,
                                          borderBottom: "1px solid #F3F4F6",
                                          cursor: "pointer",
                                          transition: "color 0.2s",
                                          color: isExpanded
                                            ? "#FF5722"
                                            : "#1C1917",
                                        }}
                                        onClick={() =>
                                          handleBhkExpand(project.id, bhkType)
                                        }
                                      >
                                        {/* Column 1: BHK Type with Icon */}
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                          }}
                                        >
                                          <Typography
                                            variant="subtitle2"
                                            sx={{ fontWeight: 700 }}
                                          >
                                            {bhkType}
                                          </Typography>
                                          {isExpanded ? (
                                            <ExpandLessIcon
                                              sx={{
                                                fontSize: 18,
                                                color: "#FF5722",
                                              }}
                                            />
                                          ) : (
                                            <ExpandMoreIcon
                                              sx={{
                                                fontSize: 18,
                                                color: "#FF5722",
                                              }}
                                            />
                                          )}
                                        </Box>

                                        {/* Column 2: Area (Centered) */}
                                        <Typography
                                          variant="body2"
                                          color={
                                            isExpanded
                                              ? "#FF5722"
                                              : "text.secondary"
                                          }
                                          sx={{
                                            textAlign: "center",
                                            fontWeight: isExpanded ? 500 : 400,
                                          }}
                                        >
                                          {plans.length === 1
                                            ? `${plans[0].area} sqft`
                                            : `${Math.min(...plans.map((p) => p.area))} - ${Math.max(
                                                ...plans.map((p) => p.area),
                                              )} sqft`}
                                        </Typography>

                                        {/* Column 3: Price (Right-aligned) */}
                                        <Typography
                                          variant="subtitle2"
                                          sx={{
                                            fontWeight: 700,
                                            textAlign: "right",
                                            color: isExpanded
                                              ? "#FF5722"
                                              : "inherit",
                                          }}
                                        >
                                          {plans.length === 1
                                            ? formatPrice(
                                                plans[0].price,
                                                plans[0].priceSuffix,
                                              )
                                            : `${formatPrice(
                                                Math.min(
                                                  ...plans.map((p) => p.price),
                                                ),
                                                plans[0].priceSuffix,
                                              )} - ${formatPrice(
                                                Math.max(
                                                  ...plans.map((p) => p.price),
                                                ),
                                                plans[0].priceSuffix,
                                              )}`}
                                        </Typography>
                                      </Box>

                                      <Collapse
                                        in={isExpanded}
                                        timeout="auto"
                                        unmountOnExit
                                      >
                                        <Box
                                          sx={{
                                            borderBottom: "1px solid #F3F4F6",
                                          }}
                                        >
                                          {plans.map((plan, pIdx) => (
                                            <Box
                                              key={plan.id || pIdx}
                                              sx={{
                                                display: "grid",
                                                gridTemplateColumns:
                                                  "120px 1fr 1fr",
                                                alignItems: "center",
                                                py: 1,
                                                pl: 1.5,
                                              }}
                                            >
                                              {/* Column 1: Indented BHK type plain text */}
                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ pl: 1 }}
                                              >
                                                {plan.bhkType}
                                              </Typography>

                                              {/* Column 2: Center-aligned Area */}
                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ textAlign: "center" }}
                                              >
                                                {plan.area} sqft
                                              </Typography>

                                              {/* Column 3: Right-aligned Price */}
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontWeight: 500,
                                                  textAlign: "right",
                                                  color: "#1C1917",
                                                }}
                                              >
                                                {formatPrice(
                                                  plan.price,
                                                  plan.priceSuffix,
                                                )}
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Box>
                                      </Collapse>
                                    </React.Fragment>
                                  );
                                },
                              )}
                            </Box>
                          );
                        })()}
                      </Box>

                      {/* Offers */}
                      {Number(project.offerPrice) > 0 && (
                        <Box
                          sx={{
                            bgcolor: "#FEF2F2",
                            p: 1.5,
                            borderRadius: "8px",
                            border: "1px dashed #FCA5A5",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="error.main"
                            sx={{ fontWeight: 600 }}
                          >
                            Exciting Offer: ₹ {formatNumber(project.offerPrice)}{" "}
                            for {project.offerMonths} Months
                          </Typography>
                        </Box>
                      )}

                      {/* Buttons */}
                      <Stack
                        direction="row"
                        spacing={1.5}
                        sx={{ mt: "auto", pt: 1.5, width: "100%" }}
                      >
                        <Button
                          variant="contained"
                          size="medium"
                          startIcon={<VisibilityIcon />}
                          onClick={() => {
                            if (project.isActive === false) return;
                            const isPlot = filters.propertyType === "Plot";
                            navigate(
                              `/propertydetails?project=${project.id}${isPlot ? "&type=plot" : ""}`,
                            );
                          }}
                          sx={{
                            textTransform: "uppercase",
                            borderRadius: "8px",
                            bgcolor: "#FF5722",
                            color: "white",
                            fontWeight: 700,
                            px: 3,
                            "&:hover": {
                              bgcolor: "#E64A19",
                            },
                          }}
                        >
                          View Details
                        </Button>
                        <Button
                          variant="outlined"
                          size="medium"
                          startIcon={<PhoneIcon />}
                          onClick={() => handleCallbackOpen(project)}
                          sx={{
                            textTransform: "uppercase",
                            borderRadius: "8px",
                            borderColor: "#FF5722",
                            color: "#FF5722",
                            fontWeight: 700,
                            px: 3,
                            "&:hover": {
                              borderColor: "#E64A19",
                              bgcolor: "#FFF5F2",
                            },
                          }}
                        >
                          Get Callback
                        </Button>
                      </Stack>
                    </Box>

                    {/* Right Column: Pricing & Rera Info */}
                    {!isSmallScreen && (
                      <Box
                        sx={{
                          width: 180,
                          flexShrink: 0,
                          p: 2,
                          borderLeft: "1px solid #F3F4F6",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          alignItems: "flex-end",
                          textAlign: "right",
                        }}
                      >
                        <Box>
                          {filters.propertyType === "Plot" && project.price && (
                            <Typography
                              variant="h6"
                              sx={{ color: "error.main", fontWeight: 800, whiteSpace: "nowrap" }}
                            >
                              {project.price}
                            </Typography>
                          )}
                          {filters.propertyType !== "Plot" &&
                            project.minPrice &&
                            project.maxPrice && (
                              <Typography
                                variant="h6"
                                sx={{ color: "error.main", fontWeight: 800, whiteSpace: "nowrap" }}
                              >
                                {formatPrice(
                                  project.minPrice,
                                  project.priceSuffix,
                                )}{" "}
                                -{" "}
                                {formatPrice(
                                  project.maxPrice,
                                  project.priceSuffix,
                                )}
                              </Typography>
                            )}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            {project.groupName}
                          </Typography>
                        </Box>

                        {project.reraId && (
                          <Box
                            sx={{
                              border: "1px solid #10B981",
                              px: 1,
                              py: 0.5,
                              borderRadius: "4px",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#10B981", fontWeight: 700 }}
                            >
                              RERA APPROVED
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                ))
              )}
            </Stack>
          </Box>

          {/* Pagination */}
          {residentialData.length > 0 &&
            !cardLoading &&
            pagination.totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.currentPage}
                  onChange={handlePageChange}
                  color="error"
                  size="large"
                />
              </Box>
            )}
        </Container>
      </Box>

      {/* Callback Dialog Form */}
      <Dialog
        open={callbackOpen}
        onClose={handleCallbackClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>
          Request a Callback
        </DialogTitle>
        <form onSubmit={handleCallbackSubmit}>
          <DialogContent sx={{ pt: 1 }}>
            {callbackError && (
              <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {callbackError}
              </Typography>
            )}
            {callbackSuccess && (
              <Typography color="success.main" variant="body2" sx={{ mb: 2 }}>
                {callbackSuccess}
              </Typography>
            )}
            <TextField
              fullWidth
              size="small"
              label="Your Name"
              name="name"
              value={callbackForm.name}
              onChange={handleCallbackChange}
              margin="dense"
              required
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <FormControl sx={{ minWidth: 100 }} size="small">
                <InputLabel>Code</InputLabel>
                <Select
                  name="countryCode"
                  value={callbackForm.countryCode}
                  onChange={handleCallbackChange}
                  label="Code"
                  MenuProps={{ disableScrollLock: true }}
                >
                  <MenuItem value="+91">+91 (IN)</MenuItem>
                  <MenuItem value="+1">+1 (US)</MenuItem>
                  <MenuItem value="+44">+44 (UK)</MenuItem>
                  <MenuItem value="+971">+971 (UAE)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                size="small"
                label="Phone Number"
                name="phone"
                value={callbackForm.phone}
                onChange={handleCallbackChange}
                type="tel"
                inputProps={{ maxLength: 10 }}
                required
              />
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 2 }}
            >
              Our sales advisor will call you within 30 minutes.
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={callbackForm.consent}
                  onChange={(e) =>
                    setCallbackForm({
                      ...callbackForm,
                      consent: e.target.checked,
                    })
                  }
                  required
                />
              }
              label={
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  I agree to receive communications on Phone/WhatsApp/Email.
                </Typography>
              }
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={handleCallbackClose}
              color="inherit"
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="error"
              sx={{ textTransform: "none" }}
            >
              Request Callback
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Footer />
      <ScrollToTopButton />
    </Box>
  );
};

export default Realestate;

const SearchFilterItem = ({ label, value, onChange, options = [] }) => {
  return (
    <FormControl
      sx={{
        width: { xs: "100%", lg: "235.5px" },
        height: "63px",
      }}
    >
      <Typography
        sx={{
          fontSize: "10px",
          fontWeight: 700,
          color: "#A8A29E",
          fontFamily: "Inter",
          letterSpacing: "0.5px",
          mb: "4px",
          textAlign: "left",
          pl: "8px",
        }}
      >
        {label}
      </Typography>
      <Select
        value={value}
        onChange={onChange}
        variant="outlined"
        sx={{
          height: "36px",
          backgroundColor: "#F9FAFB",
          borderRadius: "4px",
          fontFamily: "Inter",
          fontSize: "14px",
          fontWeight: 600,
          color: "#1C1917",
          textAlign: "left",
          "& .MuiSelect-select": {
            textAlign: "left",
            paddingLeft: "8px",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#E7E5E4",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#D6D3D1",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#B02F00",
          },
        }}
        MenuProps={{
          disableScrollLock: true,
          PaperProps: {
            sx: {
              textAlign: "left",
            },
          },
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{ textAlign: "left" }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
